import { ShieldAlert, ShieldCheck } from 'lucide-react';
import { useEffect, useState } from 'react';
import { fetchThreatSnapshot } from '@/api/threat';

/**
 * Step-6A Threat Status Badge.
 *
 * Renders in every administrative screen (sticky header, top-bar, mobile
 * drawer header). Two states:
 *  - STABLE   → static, clean gold-outline pill.
 *  - CRITICAL → high-visibility flashing crimson + Steelers-Gold outline
 *               that pulses, with an alert siren glyph.
 *
 * The badge is a button. Clicking it routes the operator straight to the
 * Threat Telemetry subpage via the ``onOpen`` callback supplied by AdminApp.
 */
const POLL_INTERVAL_MS = 60000;

export const ThreatBadge = ({ onOpen, compact = false }) => {
  const [snap, setSnap] = useState(null);

  useEffect(() => {
    let alive = true;
    const tick = async () => {
      try {
        const data = await fetchThreatSnapshot();
        if (alive) setSnap(data);
      } catch {
        // Swallow — the admin context already surfaces auth failures.
      }
    };
    tick();
    const id = setInterval(tick, POLL_INTERVAL_MS);
    return () => {
      alive = false;
      clearInterval(id);
    };
  }, []);

  const underAttack = snap?.system_under_attack === true;
  const lag = snap?.event_loop?.current_lag_ms;
  const blocks = snap?.block_window?.blocks_in_window;

  const baseClass = compact ? 'sc-threat-badge sc-threat-badge--compact' : 'sc-threat-badge';

  return (
    <button
      type="button"
      onClick={onOpen}
      className={`${baseClass} ${underAttack ? 'sc-threat-badge--alert' : 'sc-threat-badge--stable'}`}
      data-testid="threat-badge"
      data-state={underAttack ? 'alert' : 'stable'}
      aria-label={
        underAttack ? 'Critical advisory: DDoS flood detected. Click to open threat telemetry.' : 'System status stable. Click to open threat telemetry.'
      }
      title={underAttack ? snap?.reason || 'DDoS flood detected' : 'Click to open Threat Telemetry'}
    >
      {underAttack ? (
        <>
          <span className="sc-threat-badge__pulse" aria-hidden="true" />
          <ShieldAlert className="sc-threat-badge__icon" strokeWidth={2.6} />
          <span className="sc-threat-badge__label">{compact ? 'DDoS' : '⚠️ CRITICAL ADVISORY: DDoS FLOOD DETECTED'}</span>
          {!compact && (
            <span className="sc-threat-badge__meta">
              {lag != null ? `${Math.round(lag)}ms` : '—'} · {blocks ?? 0} blocks/10s
            </span>
          )}
        </>
      ) : (
        <>
          <ShieldCheck className="sc-threat-badge__icon" strokeWidth={2.4} />
          <span className="sc-threat-badge__label">{compact ? 'STABLE' : 'SYSTEM STATUS: STABLE'}</span>
          {!compact && lag != null && <span className="sc-threat-badge__meta">{Math.round(lag)}ms</span>}
        </>
      )}
    </button>
  );
};

export default ThreatBadge;
